import { useRef, useState, useEffect, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Grid,
	TextField,
	Typography,
	Paper,
	LinearProgress,
	Stack,
	Divider,
	alpha,
	useTheme,
} from "@mui/material";
import {
	Close as CloseIcon,
	Payments as PaymentsIcon,
	AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import { createFilterOptions, Util } from "../../util/Util";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { useAppSocket } from "../../providers/AppSocketContext";
import RefreshButton from "../../components/RefreshButton";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { excludeWalletPayModes, MYSQL_LIMITS } from "../../util/constants";
import { MONEY_LIMITS, roundMoney, addMoney } from "../../util/quantity";
import { Z_INDEX } from "../../styles/zIndex";
import { businessTodayDate, parseISODay } from "../../util/businessClock";

interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	editMode?: boolean;
	onSuccess?: () => void;
}

interface PaymentMode {
	id: string;
	mode_name: string;
	is_active: boolean;
}

export interface SelectedPayments {
	[key: string]: {
		enabled: boolean;
		amount: number;
	};
}

const SupplierPaymentModal = ({ isOpen, toggle, bundle, editMode, onSuccess }: Props) => {
	const [selectedPayments, setSelectedPayments] = useState<SelectedPayments>({});
	const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
	const [selectedSupply, setSelectedSupply] = useState<any>(null);
	const [selectedDate, setSelectedDate] = useState<Date>(() => businessTodayDate());
	const [payDetails, setPayDetails] = useState<string>("");
	const [isSupplyLoading, setIsSupplyLoading] = useState<boolean>(false);
	const [isSupplierLoading, setIsSupplierLoading] = useState<boolean>(false);
	const [isPaymentOptionsLoading, setIsPaymentOptionsLoading] = useState<boolean>(false);

	const errorMessageRef = useRef<string>("");

	const [situations, setSituations] = useState<any[]>([]);
	const [suppliers, setSuppliers] = useState<any[]>([]);
	const [payModes, setPayModes] = useState<PaymentMode[]>([]);
	const [selectedBalance, setSelectedBalance] = useState<number>(0);
	const { appSettings } = useAppSocket();
	const theme = useTheme();
	const themeColor = appSettings?.UI_THEME_PRIMARY?.value || theme.palette.primary.main;
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";

	const [errorInput, setErrorInput] = useState("");
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);

	const getSafePositiveAmount = (amount: number | undefined) =>
		Number.isFinite(amount) && (amount as number) > 0 ? (amount as number) : 0;

	const totalEnteredAmount = Object.values(selectedPayments)
		.filter((payment) => payment.enabled)
		.reduce((sum, payment) => addMoney(sum, getSafePositiveAmount(payment.amount)), 0);

	const canPay = Math.max(0, roundMoney(selectedBalance - totalEnteredAmount));
	const remainingBalance = canPay;
	const paymentProgress = selectedBalance > 0
		? Math.min(100, (totalEnteredAmount / selectedBalance) * 100)
		: 0;

	const { api, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();

	const isReturnSupply = Boolean(
		selectedSupply
		&& (
			Util.compareStrings(String(selectedSupply.sup_type || ""), "Return")
			|| String(selectedSupply.batch || "").startsWith("ret:")
		),
	);
	const minDate = selectedSupply?.date ? (parseISODay(selectedSupply.date) as Date) : businessTodayDate();
	const canValidate = selectedDate && totalEnteredAmount > 0 && totalEnteredAmount <= selectedBalance;

	const formatMoney = (value: number) => Util.formatCurrency(value, currency);

	const supplyLabel = (supply: any) => {
		if (!supply) return "";
		const dateLabel = supply.date
			? Util.formatDate(supply.date, "medium")
			: "";
		return `${dateLabel} · ${formatMoney(supply.amount)} · ${supply.reference || supply.batch || ""}`;
	};

	const fetchPaymentOptions = async () => {
		setIsPaymentOptionsLoading(true);

		const res = await api.get("/payment/modes/active");

		if (res) {
			if (res.status === 200) {
				const modes = excludeWalletPayModes(res.data as PaymentMode[]);
				modes.sort((a: any, b: any) => a.mode_name.localeCompare(b.mode_name));
				setPayModes(modes);

				const initialPayments: SelectedPayments = {};
				modes.forEach((mode: any) => {
					if (bundle && bundle?.mode && Util.compareStrings(bundle.mode, mode.mode_name)) {
						initialPayments[mode.id] = { enabled: true, amount: bundle.amount };
					} else {
						initialPayments[mode.id] = { enabled: false, amount: 0 };
					}
				});

				setSelectedPayments(initialPayments);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchPaymentOptions);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsPaymentOptionsLoading(false);
	};

	const fetchSupplies = async () => {
		if (!selectedSupplier?.sup_name) return;
		setIsSupplyLoading(true);

		const res = await api.get(
			`/supplies/situations/suppliers/${selectedSupplier.sup_name}/${bundle?.batch ?? ""}`,
		);

		if (res) {
			if (res.status === 200) {
				const rows = Array.isArray(res.data) ? res.data : [res.data];
				rows.sort(
					(a: any, b: any) =>
						new Date(b.date || b.Date).getTime() - new Date(a.date || a.Date).getTime(),
				);

				const normalized = rows.map((supply: any) => ({
					...supply,
					amount: Math.abs(supply.amount),
					balance: Math.abs(supply.balance),
				}));

				setSituations(normalized);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchSupplies);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsSupplyLoading(false);
	};

	const fetchSuppliers = async () => {
		setIsSupplierLoading(true);

		const res = await api.get("/supplier");

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.sup_name.localeCompare(b.sup_name));
				setSuppliers(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchSuppliers);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsSupplierLoading(false);
	};

	useEffect(() => {
		if (!isOpen) return;

		if (bundle?.supplier) {
			setSelectedSupplier({ sup_name: bundle.supplier });
		} else if (!bundle) {
			fetchSuppliers();
		}

		fetchPaymentOptions();
		setSelectedDate(new Date());
		setPayDetails(editMode ? bundle?.description || "" : "");
	}, [isOpen]);

	useEffect(() => {
		if (situations.length > 0) {
			const match = bundle?.batch
				? situations.find((s) => s.batch === bundle.batch) ?? situations[0]
				: situations[0];
			setSelectedSupply(match);
		} else {
			setSelectedSupply(null);
		}
	}, [situations, bundle?.batch]);

	useEffect(() => {
		if (selectedSupplier) {
			fetchSupplies();
		} else {
			setSituations([]);
			setSelectedSupply(null);
		}
	}, [selectedSupplier?.sup_name]);

	useEffect(() => {
		if (selectedSupply) {
			if (editMode && bundle) {
				setSelectedBalance(Number(selectedSupply.balance) + Number(bundle.amount));
			} else {
				setSelectedBalance(Number(selectedSupply.balance) || 0);
			}
		} else {
			setSelectedBalance(0);
		}
	}, [selectedSupply, editMode, bundle]);

	const onToggleMode = (id: string) => {
		setSelectedPayments((prev) => ({
			...prev,
			[id]: {
				...prev[id],
				enabled: !prev[id].enabled,
				amount: prev[id].enabled ? 0 : Math.max(0, addMoney(remainingBalance, getSafePositiveAmount(prev[id]?.amount))),
			},
		}));
	};

	const onAmountChange = (id: string, newAmount: number) => {
		if (!Number.isFinite(newAmount)) {
			setSelectedPayments((prev) => ({
				...prev,
				[id]: { ...prev[id], amount: 0 },
			}));
			return;
		}

		newAmount = roundMoney(newAmount);
		if (newAmount < 0.01 || newAmount > MONEY_LIMITS.max) {
			setErrorInput(id);
			errorMessageRef.current = `Amount must be between 0.01 and ${Util.formatNumber(MONEY_LIMITS.max)}!`;
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = "";
			}, 2000);
			return;
		}

		const newTotal = roundMoney(Object.values(selectedPayments)
				.filter((payment) => payment.enabled)
				.reduce((sum, payment) => addMoney(sum, getSafePositiveAmount(payment.amount)), 0) -
			getSafePositiveAmount(selectedPayments[id]?.amount) +
			newAmount);

		if (newTotal > selectedBalance) {
			setErrorInput(id);
			errorMessageRef.current = `Amount to pay exceeds supply balance of: ${Util.formatNumber(selectedBalance)}!`;
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = "";
			}, 3000);
			return;
		}

		setSelectedPayments((prev) => ({
			...prev,
			[id]: { ...prev[id], amount: newAmount },
		}));
	};

	const payFullBalance = () => {
		if (!selectedSupply || selectedBalance <= 0 || payModes.length === 0) return;
		const targetId = payModes[0].id;
		const next: SelectedPayments = {};
		payModes.forEach((mode) => {
			next[mode.id] = {
				enabled: mode.id === targetId,
				amount: mode.id === targetId ? selectedBalance : 0,
			};
		});
		setSelectedPayments(next);
	};

	const handlePayment = async () => {
		if (!Util.checkDating(Util.formatISODate(selectedDate), access, 'supply_payment')) {
			return;
		}

		const payload = Object.entries(selectedPayments)
			.filter(([_, payment]) => payment.enabled && payment.amount > 0)
			.map(([modeId, payment]) => ({
				batch: selectedSupply.batch,
				amount: payment.amount,
				pay_date: Util.formatISODate(selectedDate),
				mode: parseInt(modeId, 10),
				description: payDetails,
			}));

		setLoadingMessage(editMode ? "UPDATE" : "SAVE");

		const res = !editMode
			? await api.post("/supplier-payments", payload)
			: await api.put(`/supplier-payments/${bundle.id}`, payload);

		if (res) {
			if (res.status === 201 || res.status === 204) {
				if (bundle) {
					toggle();
					onSuccess?.();
				} else {
					fetchSupplies();
					toast.success(t("common:toast.success.payment"), { autoClose: 3000 });
				}
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.integrity"), { autoClose: 3000 });
			} else {
				console.error("Failed to send data:", res);
			}
		}

		setLoadingMessage(null);
	};

	const summaryItems = useMemo(
		() =>
			selectedSupply
				? [
						{ label: t("common:payments.modal.supplyDate"), value: Util.formatDate(selectedSupply.date, "medium") },
						{ label: t("common:tables.amount"), value: formatMoney(selectedSupply.amount) },
						{ label: t("common:payments.modal.paid"), value: formatMoney(selectedSupply.paid ?? 0) },
						{ label: t(isReturnSupply ? "common:payments.modal.balanceToRefund" : "common:tables.owing"), value: formatMoney(selectedSupply.balance) },
						{ label: t("common:tables.reference"), value: selectedSupply.reference || "—" },
						{ label: t("common:report.supplyBy"), value: selectedSupply.user || "—" },
					]
				: [],
		[selectedSupply, t, currency],
	);

	const panelSx = {
		p: 2,
		borderRadius: 2.5,
		border: "1px solid",
		borderColor: "divider",
		boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
		height: "100%",
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={toggle}
				maxWidth="lg"
				fullWidth
				sx={{ zIndex: Z_INDEX.dialog }}
				disableEscapeKeyDown
				PaperProps={{
					sx: {
						borderRadius: 3,
						borderTop: `3px solid ${themeColor}`,
						overflow: "hidden",
					},
				}}
			>
				<DialogTitle
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 2,
						pb: 1,
						background: `linear-gradient(180deg, ${alpha(themeColor, 0.07)} 0%, transparent 100%)`,
					}}
				>
					<Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								bgcolor: alpha(themeColor, 0.12),
								color: themeColor,
								flexShrink: 0,
							}}
						>
							<PaymentsIcon />
						</Box>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="h6" fontWeight={700} color={themeColor} noWrap>
								{t(isReturnSupply
									? "common:payments.modal.supplierRefundTitle"
									: "common:payments.modal.supplierTitle")}
							</Typography>
							<Typography variant="body2" color="text.secondary" noWrap>
								{selectedSupplier?.sup_name || t("common:payments.modal.selectSupplier")}
							</Typography>
						</Box>
					</Stack>
					<IconButton onClick={toggle} aria-label={t("common:ui.buttons.cancel")} sx={{ mt: -0.5 }}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ pt: 1 }}>
					{selectedSupply && (
						<Paper
							elevation={0}
							sx={{
								...panelSx,
								mb: 2,
								bgcolor: alpha(themeColor, 0.03),
								borderColor: alpha(themeColor, 0.2),
							}}
						>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								justifyContent="space-between"
								alignItems={{ xs: "flex-start", sm: "center" }}
								spacing={1}
								sx={{ mb: 1.5 }}
							>
								<Box>
									<Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
										{t("common:report.supplyDetails")}
									</Typography>
									<Typography variant="subtitle1" fontWeight={700}>
										{selectedSupply.reference || selectedSupply.batch}
									</Typography>
								</Box>
								<Button
									size="small"
									variant="outlined"
									onClick={payFullBalance}
									disabled={!selectedSupply || selectedBalance <= 0 || isPaymentOptionsLoading}
								>
									{t(isReturnSupply
										? "common:payments.modal.refundFullBalance"
										: "common:payments.modal.payFullBalance")}
								</Button>
							</Stack>

							<Grid container spacing={1.5} sx={{ mb: 1.5 }}>
								{summaryItems.map((item) => (
									<Grid key={item.label} size={{ xs: 6, sm: 4, md: 2 }}>
										<Typography variant="caption" color="text.secondary" display="block">
											{item.label}
										</Typography>
										<Typography variant="body2" fontWeight={600} noWrap title={String(item.value)}>
											{item.value}
										</Typography>
									</Grid>
								))}
							</Grid>

							<Box>
								<Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
									<Typography variant="caption" color="text.secondary">
										{t("common:payments.modal.allocationProgress")}
									</Typography>
									<Typography variant="caption" fontWeight={700}>
										{formatMoney(totalEnteredAmount)} / {formatMoney(selectedBalance)}
									</Typography>
								</Stack>
								<LinearProgress
									variant="determinate"
									value={paymentProgress}
									sx={{
										height: 8,
										borderRadius: 999,
										bgcolor: alpha(themeColor, 0.12),
										"& .MuiLinearProgress-bar": {
											borderRadius: 999,
											bgcolor: totalEnteredAmount > selectedBalance ? "error.main" : themeColor,
										},
									}}
								/>
							</Box>
						</Paper>
					)}

					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 7 }}>
							<Paper elevation={0} sx={panelSx}>
								<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
									{t("common:payments.modal.paymentDetails")}
								</Typography>

								<Stack spacing={2}>
									<Autocomplete
										id="supplierInput"
										size="small"
										options={suppliers}
										filterOptions={createFilterOptions((supplier) => supplier.sup_name || "")}
										getOptionLabel={(supplier) => supplier.sup_name || ""}
										value={selectedSupplier}
										onChange={(_, newValue) => setSelectedSupplier(newValue)}
										loading={isSupplierLoading}
										autoHighlight
										disabled={isSupplierLoading || !!bundle}
										renderInput={(params) => (
											<TextField
												{...params}
												label={
													<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
														{t("common:tables.supplier")}
														<RefreshButton
															isWorking={isSupplierLoading}
															onClick={fetchSuppliers}
															disabled={isSupplierLoading || !!bundle}
														/>
													</Box>
												}
												variant="filled"
												fullWidth
											/>
										)}
									/>

									<Autocomplete
										id="supplyInput"
										size="small"
										options={situations}
										filterOptions={createFilterOptions((supply) => supplyLabel(supply))}
										getOptionLabel={(supply) => supplyLabel(supply)}
										value={selectedSupply}
										onChange={(_, newValue) => setSelectedSupply(newValue)}
										loading={isSupplyLoading}
										autoHighlight
										disabled={isSupplyLoading || !selectedSupplier || !!bundle?.batch}
										renderInput={(params) => (
											<TextField
												{...params}
												label={
													<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
														{t("common:supplySituation.reference")}
														<RefreshButton
															isWorking={isSupplyLoading}
															onClick={fetchSupplies}
															disabled={isSupplyLoading || !selectedSupplier}
														/>
													</Box>
												}
												variant="filled"
												fullWidth
												helperText={
													selectedSupply
														? `${t(isReturnSupply ? "common:payments.modal.balanceToRefund" : "common:payments.modal.balanceToPay")}: ${formatMoney(addMoney(remainingBalance, totalEnteredAmount))} · ${t("common:payments.modal.remaining")}: ${formatMoney(remainingBalance)}`
														: t(isReturnSupply ? "common:payments.modal.selectReturn" : "common:payments.modal.selectSupply")
												}
											/>
										)}
									/>

									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											label={
												<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
													{t("common:ui.labels.date")}
													<RefreshButton isWorking={false} onClick={() => setSelectedDate(new Date())} />
												</Box>
											}
											value={selectedDate}
											minDate={minDate}
											onChange={(newDate) => {
												if (
													!newDate ||
													Util.compareStrings(newDate.toString(), "Invalid Date") ||
													newDate < minDate
												) {
													setErrorInput("dateInput");
													setTimeout(() => setErrorInput(""), 1500);
													return;
												}
												const date = newDate instanceof Date ? newDate : newDate.toDate();
												setSelectedDate(date);
											}}
											disabled={!selectedSupply}
											slotProps={{
												textField: {
													id: "dateInput",
													size: "small",
													fullWidth: true,
													variant: "filled",
													error: errorInput === "dateInput",
													helperText:
														errorInput === "dateInput" ? t("common:validation.invalidDate") : "",
												},
											}}
										/>
									</LocalizationProvider>

									<TextField
										id="detailsInput"
										size="small"
										label={t("common:ui.labels.notes")}
										variant="filled"
										fullWidth
										multiline
										minRows={2}
										value={payDetails}
										inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
										onChange={(e) => setPayDetails(e.target.value)}
									/>
								</Stack>
							</Paper>
						</Grid>

						<Grid size={{ xs: 12, md: 5 }}>
							<Paper elevation={0} sx={panelSx}>
								<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
									<Typography variant="subtitle2" fontWeight={700}>
										{t("common:ui.labels.paymentOptions")}
									</Typography>
									<RefreshButton
										isWorking={isPaymentOptionsLoading}
										onClick={fetchPaymentOptions}
										disabled={isPaymentOptionsLoading}
									/>
								</Stack>

								<Stack spacing={1}>
									{payModes.map((mode) => {
										const enabled = selectedPayments[mode.id]?.enabled || false;
										return (
											<Box
												key={mode.id}
												onClick={() => !isPaymentOptionsLoading && onToggleMode(mode.id)}
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													p: 1,
													borderRadius: 2,
													border: "1px solid",
													borderColor: enabled
														? alpha(themeColor, 0.45)
														: "divider",
													bgcolor: enabled ? alpha(themeColor, 0.06) : "transparent",
													cursor: isPaymentOptionsLoading ? "default" : "pointer",
													transition: "border-color 0.2s, background-color 0.2s",
													"&:hover": isPaymentOptionsLoading
														? undefined
														: { bgcolor: alpha(themeColor, 0.08) },
												}}
											>
												<Checkbox
													checked={enabled}
													onChange={() => onToggleMode(mode.id)}
													disabled={isPaymentOptionsLoading}
													onClick={(e) => e.stopPropagation()}
													sx={{ p: 0.5 }}
												/>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Typography variant="body2" fontWeight={600} noWrap>
														{mode.mode_name}
													</Typography>
												</Box>
												<TextField
													size="small"
													variant="filled"
													type="number"
													value={
														Number.isFinite(selectedPayments[mode.id]?.amount)
															? selectedPayments[mode.id]?.amount
															: 0
													}
													required={enabled}
													disabled={!enabled || isPaymentOptionsLoading}
													onFocus={(e) => e.target.select()}
													onClick={(e) => e.stopPropagation()}
													onChange={(e) => onAmountChange(mode.id, Number(e.target.value))}
													error={errorInput === mode.id}
													helperText={errorInput === mode.id ? errorMessageRef.current : " "}
													sx={{ width: { xs: 110, sm: 130 } }}
													slotProps={{
														htmlInput: { min: 0, step: 0.01, style: { textAlign: "right" } },
													}}
												/>
											</Box>
										);
									})}
								</Stack>

								<Divider sx={{ my: 1.5 }} />

								<Stack spacing={1}>
									<Stack direction="row" justifyContent="space-between" alignItems="center">
										<Typography variant="body2" color="text.secondary">
											{t("common:ui.labels.totalToPay")}
										</Typography>
										<Chip
											icon={<WalletIcon sx={{ fontSize: "16px !important" }} />}
											label={formatMoney(totalEnteredAmount)}
											color={totalEnteredAmount > selectedBalance ? "error" : "success"}
											size="small"
											sx={{ fontWeight: 700 }}
										/>
									</Stack>
									<Stack direction="row" justifyContent="space-between" alignItems="center">
										<Typography variant="body2" color="text.secondary">
											{t("common:payments.modal.remaining")}
										</Typography>
										<Typography
											variant="body2"
											fontWeight={700}
											color={remainingBalance === 0 && totalEnteredAmount > 0 ? "success.main" : "text.primary"}
										>
											{formatMoney(remainingBalance)}
										</Typography>
									</Stack>
								</Stack>
							</Paper>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions
					sx={{
						px: 3,
						py: 2,
						borderTop: "1px solid",
						borderColor: "divider",
						bgcolor: alpha(theme.palette.background.default, 0.6),
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
						{selectedSupply
							? `${t("common:payments.modal.confirmHint")} ${formatMoney(totalEnteredAmount)}`
							: t(isReturnSupply ? "common:payments.modal.selectReturn" : "common:payments.modal.selectSupply")}
					</Typography>
					<Stack direction="row" spacing={1}>
						<Button color="inherit" variant="outlined" onClick={toggle}>
							{t("common:ui.buttons.cancel")}
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={() => setConfirmModalOpen(true)}
							disabled={!canValidate}
						>
							{t(isReturnSupply ? "common:ui.buttons.refund" : "common:ui.buttons.confirmPayment")}
						</Button>
					</Stack>
				</DialogActions>
			</Dialog>

			<ConfirmDialog
				isOpen={confirmModalOpen}
				setIsOpen={setConfirmModalOpen}
				title={t("common:dialog.paymentConfirmation")}
				confirmBtnColor="success"
				confirmBtnVariant="contained"
				onConfirm={handlePayment}
			/>
		</>
	);
};

export default SupplierPaymentModal;